import {useEffect, useState} from "react";
import apiService from "../ApiCallService";
import ErrorUtils from "../../utils/ErrorUtils";
import { PLIConfig, PLIConfigMetadata, PLIConfigStep, PLIConfigTemplateSection, PLISectionEditMode, PLIStepType, PLITemplateType } from "src/models/calculation-builder/pli-config/PLIConfigTemplate";
import { PLIConfigStepResponseModel } from "src/models/calculation-builder/pli-config/PLIConfigStepsResponse";
import { SavingStatus } from "src/models/common/SavingStatus";
import { CalcBuilderComment } from "src/models/calculation-builder/CalcBuilderComment";

export default class PLIConfigService {

    createNewStep(section: PLIConfigTemplateSection, sequence?: number) {
        return {
            stepId: `newStep${sequence}`,
            sequence: sequence == undefined? (section.steps || []).length + 1 : sequence,
            sectionName: section.name,
            description: '',
            isBeingEdited: true,
            isNew: true,
            expression: '',
            savingStatus: SavingStatus.Unsaved
        } as PLIConfigStep;
    }

    public static flattenSectionSteps(sections: PLIConfigTemplateSection[]) {
        return sections.reduce((a, b) => a.concat(b.steps), [] as PLIConfigStep[]);
    }

    getPLITemplates(calculationNumber?: string, query?: string) : [PLITemplateType[], boolean, string] {
        const [result, setResult] = useState([] as PLITemplateType[]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(calculationNumber: string) {
                try {
                    setLoading(true);
                    const response = await apiService.getPLITemplateTypes(calculationNumber);
                    const json = await response.json();
                    setResult(json.allPLITypes.map((x:any) => ({
                        value: x.pli,
                        description: x.pliName,
                        group: x.templateType
                    } as PLITemplateType)));
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (query != null && calculationNumber != null) {
                fetchData(calculationNumber);
            }
        }, [query, calculationNumber]);
        return [result, loading, error]
    }

    getPLIConfigMetadata(query?: string, calculationNumber?: string) : [PLIConfigMetadata | null, boolean, string] {
        const [result, setResult] = useState(null as PLIConfigMetadata | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(calculationNumber: string) {
                try {
                    setLoading(true);
                    const response = await apiService.getPLIConfigMetadata(calculationNumber);
                    const json = await response.json();
                    const pliConfig = json.pliConfiguration || {calculationNumber, isMissingConfiguration: true};
                    setResult(pliConfig);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (query != null && calculationNumber != null) {
                fetchData(calculationNumber);
            }
        }, [query, calculationNumber]);
        return [result, loading, error]
    }

    createPLIConfig(payload?: any) : [PLIConfigMetadata | null, boolean, string] {
        const [result, setResult] = useState(null as PLIConfigMetadata | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.createPLIConfig(payload);
                    const json = await response.json();
                    const pliConfig = json.pliConfiguration;
                    setResult(pliConfig);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (payload != null) {
                fetchData();
            }
        }, [payload]);
        return [result, loading, error]
    }
    
    getPLIConfig(query?: string, calculationNumber?: string, pliVersion?: number) : [PLIConfig | null, boolean, string] {
        const [result, setResult] = useState(null as PLIConfig | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(calculationNumber: string, pliVersion: number) {
                try {
                    setLoading(true);
                    const response = await apiService.getPLIConfigSteps(calculationNumber, pliVersion);
                    const json = await response.json();
                    const pliConfig = mapConfigurationStepsToPLIConfig(json.pliConfigurationSteps);
                    setResult(pliConfig);
                }
                catch (ex) {
                    console.error(ex)
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (query && calculationNumber != null && pliVersion != null) {
                fetchData(calculationNumber, pliVersion);
            }
        }, [query, calculationNumber, pliVersion]);
        return [result, loading, error]
    }
    
    savePLIConfigStep(calculationNumber?: string, step?: PLIConfigStep) : [PLIConfigStep | null, boolean, string] {
        const [result, setResult] = useState(null as PLIConfigStep | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(calculationNumber: string, step: PLIConfigStep) {
                try {
                    setLoading(true);
                    const response = await (step.isNew? 
                        apiService.createPLIConfigStep(calculationNumber, {step}): 
                        apiService.savePLIConfigStep(calculationNumber, step.stepId, {step}));
                    const json = await response.json();
                    json.step.unsavedId = step.stepId;
                    if ( step.isNew ){
                        json.step.expression = step.expression;
                        json.step.expressionTokens = step.expressionTokens;
                    }
                    setResult(json.step);
                }
                catch (ex) {
                    console.error(ex)
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (calculationNumber != null && step != null) {
                fetchData(calculationNumber, step);
            }
        }, [calculationNumber, step]);
        return [result, loading, error]
    }

    validatePLIConfig(payload: {calculationNumber: string, calculationVersion: number} | null) : [PLIConfig | null, boolean, string] {
        const [result, setResult] = useState(null as PLIConfig | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(calculationNumber: string, calculationVersion: number) {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.validatePLIConfig(calculationNumber, calculationVersion);
                    const json = await response.json();
                    const pliConfig = mapConfigurationStepsToPLIConfig(json.steps);
                    pliConfig.isValid = json.valid;
                    setResult(pliConfig);
                }
                catch (ex) {
                    console.error(ex)
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (payload != null) {
                fetchData(payload.calculationNumber, payload.calculationVersion);
            }
        }, [payload]);
        return [result, loading, error]
    }
    
    runPLIConfig(payload: {calculationNumber: string, pliVersion: number} | null) : [PLIConfigStep[], boolean, string] {
        const [result, setResult] = useState([]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(calculationNumber: string, pliVersion: number) {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.runPLIConfig(calculationNumber, pliVersion);
                    const json = await response.json();
                    setResult(json.results);
                }
                catch (ex) {
                    console.error(ex)
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (payload != null) {
                fetchData(payload.calculationNumber, payload.pliVersion);
            }
        }, [payload]);
        return [result, loading, error]
    }
    
    deletePLIConfigStep(payload: {calculationNumber: string, stepId: string} | null) : [PLIConfigStep | null, boolean, string] {
        const [result, setResult] = useState(null as PLIConfigStep | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(calculationNumber: string, stepId: string) {
                try {
                    setLoading(true);
                    const response = await apiService.deletePLIConfigStep(calculationNumber, stepId);
                    const json = await response.json();
                    setResult(json.step);
                }
                catch (ex) {
                    console.error(ex)
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (payload != null) {
                fetchData(payload.calculationNumber, payload.stepId);
            }
        }, [payload]);
        return [result, loading, error]
    }

    configurePLIForSingleCLI(payload: {calculationNumber: string} | null) : [string | null, boolean, string] {
        const [result, setResult] = useState(null as string | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(calculationNumber: string) {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.configureSingleCLI(calculationNumber);
                    const json = await response.json();
                    setResult(json.calculationNumber);
                }
                catch (ex) {
                    console.error(ex)
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (payload != null) {
                fetchData(payload.calculationNumber);
            }
        }, [payload]);
        return [result, loading, error]
    }

    clonePLIConfiguration(payload: any | null, shouldCallSaveBeforeCloning: boolean) : [any, boolean, string] {
        const [result, setResult] = useState<any>(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(payload: any) {
                try {
                    setLoading(true);
                    setError('');
                    if (shouldCallSaveBeforeCloning) {
                        const response = await apiService.configureSingleCLI(payload.calculationNumber);
                        const json = await response.json();
                    }
                    const cloneResponse = await apiService.clonePLIConfiguration(payload.calculationNumber, payload);
                    const cloneResponseJson = await cloneResponse.json();
                    setResult({calculationNumber: cloneResponseJson.sourceCalculationNumber});
                }
                catch (ex) {
                    console.error(ex)
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (payload != null) {
                fetchData(payload);
            }
        }, [payload]);
        return [result, loading, error]
    }
    
    switchPLITemplate(payload: any | null) : [PLIConfigMetadata | null, boolean, string] {
        const [result, setResult] = useState<PLIConfigMetadata | null>(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(payload: any) {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.switchPLITemplate(payload.calculationNumber, payload.newPLI);
                    const json = await response.json();
                    const pliConfig = json.newPLIConfiguration || {calculationNumber: payload.calculationNumber, isMissingConfiguration: true};
                    setResult(pliConfig);
                }
                catch (ex) {
                    console.error(ex)
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (payload != null) {
                fetchData(payload);
            }
        }, [payload]);
        return [result, loading, error]
    }

    saveStepComment(comment?: CalcBuilderComment) : [CalcBuilderComment | null, boolean, string | null] {
        const [result, setResult] = useState(null as CalcBuilderComment | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null as string | null);

        useEffect(() => {
            async function postData(payload: CalcBuilderComment) {
                try {
                    setError(null);
                    //const response = await apiService.createCalcStepComment(payload.calculationNumber, payload.calculationVersion as number, payload.stepId || '', payload.comment);
                    const json = await fakeServerCall({comment:{...payload, createdDate: new Date(), createUser: "Test user"}});
                    setResult(json.comment);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (comment != null) {
                setLoading(true);
                postData(comment);
            }
        }, [comment]);
        return [result, loading, error]
    }

    getStepComments(calculationNumber?: string, version?: number, stepId?: string, pullStepComments?: boolean) : [CalcBuilderComment[], boolean, string | null] {
        const [result, setResult] = useState([] as CalcBuilderComment[]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null as string | null);

        useEffect(() => {
            async function fetchData(calcNumber:string) {
                try {
                    setLoading(true);
                    //const response = await apiService.getCalcStepComments(calcNumber, version == null ? -1 : version, stepId || '');
                    const json = (await fakeServerCall({comments:[]})).json();
                    setResult(json.comments);
                }
                catch (ex) {       
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (calculationNumber != null && pullStepComments === true) {
                fetchData(calculationNumber);
            }
        }, [calculationNumber, pullStepComments]);
        return [result, loading, error]
    }

    saveOverviewSectionPLIConfigStepName(payload ?: any) : [string | null, boolean, string] {
        const [result, setResult] = useState(null as string | null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData(calculationNumber: string, data: any) {
                try {
                    setLoading(true);
                    const response = await apiService.savePLIConfigOverviewSectionStep(calculationNumber, data);
                    const json = await response.json();
                    setResult(json.calculationNumber);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (payload != null && payload.calculationNumber != null) {
                fetchData(payload.calculationNumber, payload);
            }
        }, [payload]);
        return [result, loading, error]
    }

    
}



async function fakeServerCall(response: any):Promise<any> {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve(response);            
        }, 1000);
    });
}

function mapConfigurationStepsToPLIConfig(allConfigSteps: PLIConfigStepResponseModel[]): PLIConfig {
    const result = {} as PLIConfig;
    result.sections = [];
    const steps = allConfigSteps.filter(x => x.type == PLIStepType.STEP);
    const sections = allConfigSteps.filter(x => x.type == PLIStepType.SECTION);
    sections.forEach((section: PLIConfigStepResponseModel) => {
        const isOverview = section.sectionName === "Overview";
        result.sections.push({
            name: section.sectionName,
            description: isOverview? "" : "Use to systematically calculate PLI",
            editMode: isOverview? PLISectionEditMode.READ_ONLY : PLISectionEditMode.EDITABLE,
            error: '',
            showValues: false,
            steps: steps.filter(s => s.sectionName === section.sectionName).map(step => ({
                calculationNumber: step.calculationNumber,
                createdDate: step.createdDate,
                createUser: step.createUser,
                description: step.description,
                expression: step.expression,
                expressionTokens: step.expressionTokens,
                isBeingEdited: false,
                isNew: false,
                lastModifiedUser: step.lastModifiedUser,
                lastModifiedDate: step.lastModifiedDate,
                pliVersion: step.pliVersion,
                savingStatus: SavingStatus.Saved,
                sectionName: step.sectionName,
                sequence: step.sequence,
                stepId: step.stepId,
                valid: step.valid,
                validationErrors: step.validationErrors,
                value: step.value,
                descriptionHelp: step.descriptionHelp,
                isConfigurableStep: step.isConfigurableStep,
                isDescriptionEditable: step.isDescriptionEditable,
                isValueEditable: step.isValueEditable,                
                hasComments: false,
                comments: step.comments,
                isMarkupRate: step.description === "Mark-up Rate"
            })),
            isOverview: isOverview
        })
    })
    return result;
}
